import React, { useMemo, memo, forwardRef } from 'react';
import cn from 'classnames';

import { Icon, IconName } from 'components';
import { Option } from 'lib/models/option';
import styles from './DateSelector.module.scss';
import { DateSelectorValues, months } from './DatePicker.component';

export type DateRange = [Date, Date] | [null, null];

export type DropdownOption = Option & {
  iconName?: IconName;
  groupEnd?: boolean;
  isDelete?: boolean;
  isDefault?: boolean;
  industry_ids?: Array<string>;
};

export interface DateSelectorProps {
  /** Custom class name for additional styling */
  className?: string;
  /** Custom style object */
  style?: Record<string, unknown>;
  /** Array of options for the dropdown */
  options?: Array<DateSelectorValues>;
  /** Handler for year change events */
  onChangeYear: (userSelectedYear: DateSelectorValues) => void;
  /** Handler for month change events */
  onChangeMonth: (userSelectedMonth: DateSelectorValues) => void;
  /** on date change handler */
  onDateChange: (option: DateSelectorValues) => void;
  /** Boolean flag to indicate if the year dropdown is open */
  isYearDropdownOpen: boolean;
  /** Boolean flag to indicate if the month dropdown is open */
  isMonthDropdownOpen: boolean;
  /** The currently selected value (year or month) */
  userSelectedValue: string | null | undefined;
  selectedYear?: number | null | undefined;
  selectedMonth?: string | null | undefined;
  onChangeMonthDropdown: (isOpen: boolean) => void;
  onChangeYearDropdown: (isOpen: boolean) => void;
  isOnboarding?: boolean | null;
}

export const DateSelector = memo(
  forwardRef<HTMLUListElement, DateSelectorProps>(
    (
      {
        className = '',
        style,
        options,
        onChangeYear,
        onDateChange,
        onChangeMonthDropdown,
        onChangeYearDropdown,
        selectedYear,
        selectedMonth,
        userSelectedValue,
        onChangeMonth,
        isYearDropdownOpen,
      },
      ref
    ) => {
      const contentClassNames = cn(styles.SingleSelect, className);
      const currentYear = new Date().getFullYear();
      const currentMonthIndex = new Date().getMonth();
      const currentMonth = months[currentMonthIndex];
      const filteredOptions = useMemo(() => {
        const list = options;
        return list;
      }, [options]);

      const onSelect = (option: DateSelectorValues) => {
        if (!selectedMonth && !selectedYear) {
          if (!isYearDropdownOpen) {
            onChangeMonth(option);
            onChangeYearDropdown(true);
            onChangeMonthDropdown(false);
          } else if (isYearDropdownOpen) {
            onChangeYear(option);
            onChangeMonthDropdown(true);
            onChangeYearDropdown(false);
          }
        } else if (!isYearDropdownOpen && selectedYear && !selectedMonth) {
          onChangeMonth(option);
          onChangeYearDropdown(false);
          onDateChange(option);
        } else if (isYearDropdownOpen && selectedYear && !selectedMonth) {
          onChangeYear(option);
          onChangeYearDropdown(false);
          onChangeMonthDropdown(true);
        } else if (!isYearDropdownOpen && selectedMonth && !selectedYear) {
          onChangeMonth(option);
          onChangeYearDropdown(true);
          onChangeMonthDropdown(false);
        } else if (isYearDropdownOpen && selectedMonth && !selectedYear) {
          onChangeYear(option);
          onChangeMonthDropdown(false);
          onDateChange(option);
        } else if (selectedMonth && selectedYear) {
          if (!isYearDropdownOpen) {
            onChangeMonth(option);
            onDateChange(option);
            onChangeYearDropdown(true);
          } else {
            onChangeYear(option);

            onDateChange(option);
            onChangeYearDropdown(false);
            onChangeMonthDropdown(false);
          }
        }
      };

      const toggleDropdown = () => {
        if (isYearDropdownOpen) {
          onChangeYearDropdown(false);
          onChangeMonthDropdown(true);
        } else {
          onChangeYearDropdown(true);
          onChangeMonthDropdown(false);
        }
      };

      return (
        <div
          className={contentClassNames}
          style={style}
          data-testid="SingleSelect"
        >
          <div className={cn(styles.SingleSelectDropdown)}>
            <ul className={styles.options} ref={ref}>
              {isYearDropdownOpen && (
                <>
                  <li
                    className={cn(styles.detailOption)}
                    onClick={() => toggleDropdown()}
                  >
                    <span>{selectedMonth ? selectedMonth : currentMonth}</span>

                    <Icon iconName="icon_arrow-down" className={styles.arrow} />
                  </li>
                  <div className={styles.separator}></div>
                </>
              )}
              {filteredOptions?.map((option) => {
                const { id, label } = option;
                const checked = userSelectedValue === label;
                return (
                  <li
                    key={id}
                    className={styles.option}
                    onClick={() => onSelect(option)}
                  >
                    <span>{label}</span>
                    {checked && (
                      <Icon
                        iconName="valid-check-mark"
                        className={styles.checkMark}
                      />
                    )}
                  </li>
                );
              })}

              {!isYearDropdownOpen && (
                <>
                  <div className={styles.separator}></div>
                  <li
                    className={cn(styles.detailOption)}
                    onClick={() => toggleDropdown()}
                  >
                    <span>{selectedYear ? selectedYear : currentYear}</span>

                    <Icon iconName="icon_arrow-down" className={styles.arrow} />
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>
      );
    }
  )
);

DateSelector.displayName = 'DateSelector';
